import React from 'react';

import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowProcedureFlow from './components/WorkflowProcedureFlow';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  {
    label: 'structure/tech',
    title: 'structure/techルールの作成',
    path: '/devdocs/handson/structure-tech',
  },
  {
    label: 'スキーマ+API仕様書',
    title: 'スキーマ定義とAPI仕様書の生成',
    path: '/devdocs/handson/schema-api',
  },
  { label: 'API実装ワークフロー', title: 'API実装ワークフローを作成する', active: true },
  { label: 'E2Eテスト', title: 'E2Eテストを実行する', path: '/devdocs/handson/e2e' },
];

const HandsonApiWorkflow: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="実装編 — API実装ワークフロー"
      seriesName="実装編"
      tags={['Hands-on', 'API', 'Workflow', 'Implementation']}
      seriesSteps={seriesSteps}
      prevLink={{
        label: 'スキーマ定義 + API仕様書の生成',
        path: '/devdocs/handson/schema-api',
      }}
      nextLink={{ label: 'E2Eテスト', path: '/devdocs/handson/e2e' }}
    >
      <WorkflowGoalBox
        prerequisites={[
          '「実装編 — スキーマ定義 + API仕様書の生成」が完了している',
          'Docker環境が起動可能（docker-compose up で MySQL + Flyway が動く）',
        ]}
        completionCriteria={[
          '.kiro/steering/api_dev_workflow.md が作成されている',
          '.kiro/steering/create_dd.md が作成されている',
          '.kiro/steering/local_test.md が作成されている',
          '少なくとも1本のAPIで Phase1（設計書）→ Phase2（コード生成）→ Phase3（テスト）を完了している',
          'アプリケーションがビルドエラーなく起動する',
          '単体テストが全ケースOKである',
          'ワークフローを1回以上改善した実績がある',
        ]}
      >
        API実装ワークフロー（api_dev_workflow.md）を作成し、1本のAPIで設計書作成→コード生成→単体テストの全工程を通しで実行する。その後、残りのAPIを同じワークフローで実装しながらルールを改善していく。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="実装編 — スキーマ定義 + API仕様書の生成"
        description="前の記事"
        path="/devdocs/handson/schema-api"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'この章で体験すること' },
          { id: 'section-2', label: 'ワークフローの3フェーズ構造' },
          { id: 'section-3', label: 'create_dd.md を作成する' },
          { id: 'section-4', label: 'local_test.md を作成する' },
          { id: 'section-5', label: 'api_dev_workflow.md を作成する' },
          { id: 'section-6', label: '1本目のAPIで全工程を通す' },
          { id: 'section-7', label: 'ワークフローを改善する' },
          { id: 'section-8', label: '残りのAPIを実装する' },
          { id: 'section-9', label: '現在のプロジェクト構成' },
          { id: 'section-10', label: 'ベストプラクティスとアンチパターン' },
          { id: 'section-11', label: 'この章で学んだこと' },
        ]}
      />

      {/* セクション1 */}
      <WorkflowSection title="この章で体験すること" id="section-1" number={1}>
        <WorkflowSection title="API仕様書からコードまでを一気通貫で自動化する" subNumber="1.1">
          <WorkflowText>
            これまでは「ルールを書く →
            AIに1つの成果物を生成させる」という単発のサイクルでした。この章では複数ステップを一連のワークフローとして定義し、AIが順序立てて実行する仕組みを体験します。
          </WorkflowText>

          <WorkflowProcedureFlow
            steps={[
              {
                label: 'Phase1: 設計書作成',
                sub: 'API仕様書を読む → 実装計画を立てる → DD書を作成する',
              },
              {
                label: 'Phase2: コード生成',
                sub: '設計書を元にJavaコードを生成 → ユーザーレビュー',
              },
              {
                label: 'Phase3: 単体テスト',
                sub: 'API仕様書からテストケースを作成 → curlでテスト実行',
                variant: 'complete',
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="なぜワークフローSteeringを作るのか" subNumber="1.2">
          <WorkflowTipBox title="ワークフローなしでも実装できるのでは？">
            <WorkflowText>
              できます。API仕様書を見せて「このAPIのコードを書いて」と指示すればAIはコードを生成します。
            </WorkflowText>
            <WorkflowText>しかしワークフローなしだと以下の問題が起きます:</WorkflowText>
            <WorkflowList
              items={[
                '毎回同じ説明を繰り返す: 「Mapperは@Selectで書いて」「Entityはこのパッケージに置いて」と毎回指示する必要がある',
                '手順の漏れが発生する: テストを忘れる、DD書を更新し忘れる、マイグレーションとの整合性チェックを飛ばす',
                '品質にバラつきが出る: APIごとに異なるパターンのコードが生成され、コードベースの一貫性が崩れる',
                'レビューポイントが曖昧: どのタイミングで何を確認すべきか毎回判断が必要',
              ]}
            />
            <WorkflowText>
              ワークフローSteering は「AIへの定型指示 + 品質ゲート +
              実行手順」を1ファイルにまとめたものです。2本目以降のAPIは 「#api_dev_workflow.md
              API002のワークフローを開始して」 の一言で全工程が自動実行されます。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="この章の流れ" subNumber="1.3">
          <WorkflowList
            variant="ordered"
            items={[
              'ワークフローの3フェーズ構造を理解する',
              'api_dev_workflow.md を作成する',
              '1本目のAPIで全工程を通しで実行する',
              'ワークフローを改善する',
              '残りのAPIを実装する（概要のみ）',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="参照する記事" subNumber="1.4">
          <WorkflowText>
            以下のAIと作る、壊れないバックエンド紹介記事に、3フェーズそれぞれの詳細な説明があります。
          </WorkflowText>
          <WorkflowLinkCard
            title="バックエンド開発編 Part2 — API実装ワークフローの全体像"
            description="ワークフロー全体像と3フェーズ構成・入力プロトコル"
            path="/devdocs/workflow/be-step2"
          />
          <WorkflowLinkCard
            title="バックエンド開発編 Part3 — Phase1: DD書の作成"
            description="Phase1: DD書の作成手順・create_dd.mdルール全文"
            path="/devdocs/workflow/be-step3"
          />
          <WorkflowLinkCard
            title="バックエンド開発編 Part4 — Phase2: コード生成"
            description="Phase2: mk_instructions→コード生成の手順"
            path="/devdocs/workflow/be-step3"
          />
          <WorkflowLinkCard
            title="バックエンド開発編 Part5 — Phase3: 単体テスト"
            description="Phase3: 設計書ベーステスト・local_test.mdルール全文"
            path="/devdocs/workflow/be-step4"
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2 */}
      <WorkflowSection title="ワークフローの3フェーズ構造" id="section-2" number={2}>
        <WorkflowSection title="全体像" subNumber="2.1">
          <WorkflowTable
            headers={['Phase', '名前', '入力', '出力', '目的']}
            rows={[
              [
                'Phase1',
                '設計書作成',
                'API仕様書 + スキーマ定義',
                '実装計画 + DD書',
                '「何をどう実装するか」を設計する',
              ],
              [
                'Phase2',
                'コード生成',
                'DD書 + 実装計画',
                'Javaソースコード',
                '設計通りにコードを生成する',
              ],
              [
                'Phase3',
                '単体テスト',
                'API仕様書（設計書ベース）',
                'テストケース + テスト結果',
                'コードが仕様通りに動くか検証する',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="各フェーズの詳細" subNumber="2.2">
          <WorkflowText>
            各フェーズの具体的なステップ・成果物・手順は紹介記事に詳しく書かれています。「参照する記事」からPart3〜5を読んでください。
          </WorkflowText>
          <WorkflowText>
            ここではワークフローの完成例を書くうえで重要なポイントだけ押さえます。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="停止ポイントの意味" subNumber="2.3">
          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              各フェーズには「停止ポイント」があります。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              AIが成果物を作成した後、必ず一度止まってユーザーの確認を待ちます。確認OKなら次のステップに進みます。
            </Typography>
          </WorkflowInfoBox>
          <WorkflowList
            items={[
              '問題なし → 「0: 問題なし、次に進んで」',
              '修正あり → 「1: ここを直して（具体的な指示）」',
            ]}
          />
          <WorkflowText>
            停止ポイントはAIの暴走を防ぐ品質ゲートです。「設計が間違ったまま大量のコードが生成される」事態を防ぎます。
          </WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3 */}
      <WorkflowSection title="create_dd.md を作成する" id="section-3" number={3}>
        <WorkflowSection title="なぜ create_dd.md が必要か" subNumber="3.1">
          <WorkflowText>
            DD書（詳細設計書）はPhase1で毎回生成されます。フォーマットが毎回微妙にブレると、Phase2のコード生成精度が下がります。
          </WorkflowText>

          <WorkflowTipBox title="Steeringだけではフォーマットがブレる問題">
            <WorkflowText>
              Steeringファイルにフォーマットを記載するだけだと、AIが解釈する度に微妙な差異（テーブル列の順序、処理セクションの粒度等）が出ることがあります。
            </WorkflowText>
            <WorkflowText>
              紹介記事のプロジェクトでは、この問題をテンプレートファイルをリポジトリにコミットして参照させる方式で解決しています。Steeringにはルール（禁止事項・命名規則等）を書き、フォーマットの実例はテンプレートファイルを見せることで「このファイルと同じ形式で作って」と指示するわけです。
            </WorkflowText>
            <WorkflowText>
              紹介記事の付録「成果物サンプル集」にテンプレートの実例（00_template/
              配下）が掲載されています。参考にしてください。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="ファイルの位置づけ" subNumber="3.2">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['ファイル', '.kiro/steering/create_dd.md'],
              ['読み込みモード', 'fileMatch（docs/dd/**/*.md）'],
              ['役割', 'DD書の作成・修正時にフォーマットと記載ルールを自動適用する'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="テンプレートファイルの配置" subNumber="3.3">
          <WorkflowText>
            Steeringと合わせて、DD書のテンプレートファイルを docs/dd/00_template/
            に配置します。まずディレクトリ構造を作成してください:
          </WorkflowText>

          <WorkflowCodeBlock
            language="bash"
            code="mkdir -p docs/dd/00_template docs/dd/controller docs/dd/service docs/dd/mapper docs/dd/model docs/dd/constant"
          />

          <WorkflowDirectoryTree
            root="docs/dd"
            entries={[
              {
                name: '00_template',
                desc: 'テンプレート（事前にコミット）',
                children: [
                  { name: 'PersonController.md' },
                  { name: 'PersonService.md' },
                  { name: 'PersonMapper.md' },
                  { name: 'Person.md' },
                  { name: 'PersonType.md' },
                ],
              },
              { name: 'controller', desc: '実際のDD書（Phase1で生成）', children: [] },
              { name: 'service', children: [] },
              { name: 'mapper', children: [] },
              { name: 'model', children: [] },
              { name: 'constant', children: [] },
            ]}
          />

          <WorkflowText>
            テンプレートは紹介記事の付録「成果物サンプル集」に掲載されている Person
            シリーズをそのままコピーして使ってください。架空の Person
            ドメインで書いてあるため、実際のTODOアプリのDD書と混同する心配がありません。
          </WorkflowText>

          <WorkflowLinkCard
            title="バックエンド開発編 付録 — 成果物サンプル集"
            description="DD書テンプレート（00_template/Person*.md）の全文が掲載されています"
            path="/devdocs/workflow/be-appendix"
          />
        </WorkflowSection>

        <WorkflowSection title="create_dd.md に書くべき情報" subNumber="3.4">
          <WorkflowTable
            headers={['セクション', '内容']}
            rows={[
              ['テンプレート参照指示', 'docs/dd/00_template/ を参照してフォーマットを揃える'],
              ['DD書の種類と配置先', 'レイヤーごとの配置ルール'],
              ['基本構造', 'class name / field / methods / 処理 の共通構造'],
              ['Mapper固有ルール', 'SQL全文記載、末尾にEntity定義'],
              ['処理セクションの粒度', '「誰が見てもコードが書ける」レベル'],
              ['禁止事項', 'テンプレートにない独自構造を追加しない等'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="3.5">
          <WorkflowText>以下を作成してください:</WorkflowText>
          <WorkflowList
            variant="ordered"
            items={[
              '.kiro/steering/create_dd.md',
              'docs/dd/00_template/ 配下のテンプレートファイル（5種類）',
            ]}
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              紹介記事のテンプレートはヘキサゴナルアーキテクチャ向けです（UseCase, Repository,
              RepositoryImpl等）。レイヤードアーキテクチャでは以下の5種類で十分です:
            </Typography>
            <WorkflowList
              items={[
                'Controller（エンドポイント + DTOマッピング）',
                'Service（ビジネスロジック）',
                'Mapper（SQL定義 + 末尾にEntity定義）',
                'Model（ドメインモデル）',
                'Enum（定数定義）',
              ]}
            />
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="create_dd.md - 完成例" subNumber="3.6">
          <WorkflowLockedCodeBlock password="Rk9cVn">
            {`---
inclusion: fileMatch
globs:
  - "docs/dd/**/*.md"
---

# DD書 作成・変更ルール

## テンプレート参照

DD書を作成・修正する際は docs/dd/00_template/ 配下のテンプレートファイルを参照し、同じフォーマットで記載する。テンプレートにない独自構造を追加しない。

## DD書の種類と配置先

| クラス種別 | 配置先 | 備考 |
|---|---|---|
| Controller | docs/dd/controller/ | エンドポイント定義・DTOマッピング |
| Service | docs/dd/service/ | ビジネスロジック |
| Mapper | docs/dd/mapper/ | SQL定義。末尾にEntity定義を記載 |
| Model | docs/dd/model/ | ドメインモデル |
| Enum | docs/dd/constant/ | 定数Enum |

## DD書の基本構造

全てのDD書は以下の共通構造を持つ。

# {クラス名}

| class name | layer | package |
|---|---|---|
| {クラス名}.java | {レイヤー} | {パッケージ} |

## components

| component |
|---|
| [{依存クラス名}](相対リンク) |

## field

| name | type | description |
|---|---|---|
| {フィールド名} | {型} | {説明} |

## methods

### #{メソッド名}

#### 引数

| name | type | description |
|---|---|---|
| {引数名} | {型} | {説明} |

#### 戻り値

| name | type | description |
|---|---|---|
| --- | {型} | {説明} |

#### 処理

1. {処理ステップ}

## 記載ルール

### 共通

- 処理セクションは「誰が見てもコードが書ける」レベルに詳細に記載する。
- 処理内で他のクラスのメソッドを呼び出す場合は クラス名#メソッド名 で記載する。
- 条件分岐がある場合は明記する。
- components には DI で注入するクラスを記載する。

### Controller

- エンドポイント情報（Method, Path）をクラス説明に記載する。
- 処理はバリデーション → Service呼び出し → レスポンス変換の流れで記載する。
- Request/Response DTOのフィールドはDD書内で定義しない（API仕様書を参照）。

### Service

- ビジネスロジックの核心部分を処理セクションに記載する。
- トランザクション境界をメソッド単位で明記する。

### Mapper

- SQL文を全文記載する（省略不可）。
- DD書の末尾に Entity の定義（field テーブル）を記載する。
- パラメータが複数の場合は @Param で名前を指定する旨を備考に記載する。

### Model

- ビジネスロジックで使用するオブジェクトのフィールドを定義する。
- Entity との変換ロジックが必要な場合は methods に記載する。

### Enum

- field テーブルに code（String）と label（表示名）を定義する。
- of(String code) staticメソッドを methods に記載する。

## 禁止事項

- テンプレートにない独自のセクションやテーブルを追加しない。
- 処理セクションを「〇〇する」の1行で済ませない。具体的なステップに分解する。
- Mapper のSQL文を省略しない。`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4 */}
      <WorkflowSection title="local_test.md を作成する" id="section-4" number={4}>
        <WorkflowSection title="なぜ local_test.md が必要か" subNumber="4.1">
          <WorkflowText>
            Phase3のテスト実行時に、Docker環境の構築・アプリケーション起動・テスト用DB接続の手順をAIに伝える必要があります。これをワークフロー内に書くとapi_dev_workflow.mdが肥大化し、Phase1・2で不要な情報まで読み込まれます。
          </WorkflowText>
          <WorkflowText>
            紹介記事と同じく、テスト実行手順は local_test.md
            として分離し、Phase3のテスト実行時にのみ #local_test.md でmanual渡しします。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="ファイルの位置づけ" subNumber="4.2">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['ファイル', '.kiro/steering/local_test.md'],
              ['読み込みモード', 'manual（Phase3テスト実行時に #local_test.md で渡す）'],
              ['役割', 'Docker環境構築・アプリ起動/停止・テスト用DB接続手順を定義する'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="書くべき情報" subNumber="4.3">
          <WorkflowTable
            headers={['セクション', '内容']}
            rows={[
              ['Docker構成', 'docker-compose.yml のサービス構成・ポート・DB名'],
              ['コンテナ確認手順', 'docker compose ps 等の確認コマンド'],
              ['テスト用DBの準備', 'Flywayでclean migrate する手順'],
              ['アプリケーション起動', 'テスト用DB接続での起動コマンド（環境変数切り替え）'],
              ['アプリケーション停止', 'テスト完了後の停止コマンド'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="4.4">
          <WorkflowText>以下のファイルを作成してください。</WorkflowText>
          <WorkflowCodeBlock language="text" code=".kiro/steering/local_test.md" />
          <WorkflowText>
            自分のプロジェクトの docker-compose.yml
            の設定（ポート番号・DB名・パスワード等）に合わせて記載してください。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="local_test.md - 完成例" subNumber="4.5">
          <WorkflowLockedCodeBlock password="Wn6jTq">
            {`---
inclusion: manual
---

# ローカルテスト実行ルール

## Docker構成

| サービス | イメージ | ホストポート | 用途 |
|----------|---------|-------------|------|
| mysql | mysql:8.0 | 13306 | ローカル開発用MySQL |
| flyway | flyway/flyway:7.15.0 | - | DBマイグレーション実行 |

### DB情報

| DB名 | 用途 |
|------|------|
| teamtodo | 通常開発用 |
| teamtodo_test | テスト実行用 |

rootパスワード: root

## コンテナの確認手順

### コンテナ状態確認

docker compose ps

- mysql が Up (healthy) であること。
- flyway が Exited (0) であること（正常終了）。

### コンテナが動いていない場合

docker compose up -d

## テスト実行手順

### 1. 既存プロセスの停止

pkill -f "bootRun" 2>/dev/null
pkill -f "GradleDaemon" 2>/dev/null
sleep 2

### 2. テスト用DBのクリーン&マイグレーション

docker compose run --rm flyway -url=jdbc:mysql://mysql:3306/teamtodo_test -user=root -password=root clean migrate

### 3. アプリケーション起動（テスト用DB接続）

DB_URL="jdbc:mysql://localhost:13306/teamtodo_test?serverTimezone=Asia/Tokyo&useSSL=false&allowPublicKeyRetrieval=true" ./gradlew bootRun > /tmp/bootrun.log 2>&1 &
sleep 20

### 4. テスト実行

test.md のcurlを順番に実行し、期待値と比較する。
結果を .kiro/specs/{API_ID}/test_result.md に出力する。

### 5. アプリケーション停止

pkill -f "bootRun" 2>/dev/null
pkill -f "GradleDaemon" 2>/dev/null

## 接続情報

### 通常開発時

| 項目 | 値 |
|------|----|
| URL | jdbc:mysql://localhost:13306/teamtodo |
| ユーザー | root |
| パスワード | root |

### テスト実行時

| 項目 | 値 |
|------|----|
| URL | jdbc:mysql://localhost:13306/teamtodo_test |
| ユーザー | root |
| パスワード | root |

環境変数 DB_URL で切り替える。`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5 */}
      <WorkflowSection title="api_dev_workflow.md を作成する" id="section-5" number={5}>
        <WorkflowSection title="ワークフローSteering の位置づけ" subNumber="5.1">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['ファイル', '.kiro/steering/api_dev_workflow.md'],
              ['読み込みモード', 'manual（ユーザーが #api_dev_workflow.md で明示的に渡す）'],
              ['起動タイミング', 'API単位で実装を開始するとき'],
            ]}
          />

          <WorkflowTipBox title="なぜ manual モードなのか">
            <WorkflowText>
              api_dev_workflow.md
              は長いルールファイルです。fileMatchにすると、API仕様書やDD書を開くたびに全文が読み込まれ、コンテキスト枠を大量に消費します。
            </WorkflowText>
            <WorkflowText>
              ワークフローが必要なのは「APIの実装を開始するとき」だけです。修正や確認だけしたい時には不要です。manualにすることで、必要な時にだけ読み込まれます。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="書くべき情報" subNumber="5.2">
          <WorkflowTable
            headers={['セクション', '内容']}
            rows={[
              ['目的', 'このワークフローが何をするか'],
              ['入力', '対象のAPI仕様書の指定方法'],
              ['作業ディレクトリ', 'specs配下のディレクトリ構造とバージョニング'],
              ['フェーズ定義', 'Phase1〜3 の各ステップと成果物'],
              ['停止ルール', 'どこで止まるか、ユーザーの入力プロトコル'],
              ['テスト実行手順', 'Docker起動〜curl実行〜停止の手順'],
            ]}
          />

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              DD書のフォーマット・記載ルールは api_dev_workflow.md 内には書きません。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              別Steeringファイル create_dd.md として分離し、docs/dd/**/*.md
              にfileMatchさせます。ワークフロー内では「DD書を作成する」と指示するだけで、フォーマットは
              create_dd.md から自動で読み込まれます。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="5.3">
          <WorkflowText>以下のファイルを作成してください。</WorkflowText>
          <WorkflowCodeBlock language="text" code=".kiro/steering/api_dev_workflow.md" />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>: 紹介記事の api_project_rule.md
              は60行以上の複雑なルールです。これはヘキサゴナルアーキテクチャ +
              複数の連動更新（API一覧、権限一覧、用語集等）を含むためです。
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              TODOアプリでは以下を省略できます:
            </Typography>
          </WorkflowInfoBox>

          <WorkflowList
            items={[
              '権限一覧の連動更新（認証なし）',
              'API一覧の連動更新（APIが少ないので不要）',
              '用語集の連動更新（チーム運用が未確立のため任意）',
              'Rv指摘・注釈の処理（同上）',
              '複数API同時指定のルール（1APIずつでOK）',
            ]}
          />

          <WorkflowText>
            また、以下は別Steeringに委譲済みです（api_dev_workflow.md には書きません）:
          </WorkflowText>
          <WorkflowList
            items={[
              'DD書のフォーマット・記載ルール → create_dd.md',
              'テスト環境の構築・実行手順 → local_test.md',
            ]}
          />

          <WorkflowText>
            代わりに「3フェーズの進行」「停止ポイント」「DD書のフォーマット」「テスト手順」に集中してください。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="api_dev_workflow.md - 完成例" subNumber="5.4">
          <WorkflowLockedCodeBlock password="Hm2xFg">
            {`---
inclusion: manual
---

# API実装ワークフロー

## 目的

指定されたAPI仕様書を対象に、設計書作成→コード生成→単体テストの3フェーズを順序立てて実行する。

## 起動方法

#api_dev_workflow.md API{ID} のワークフローを開始して

## 共通ルール

- 各フェーズは「成果物作成 → 停止 → ユーザー確認」のサイクルで進行する。
- ユーザーの入力:
  - 0: {コメント} → 問題なし。次のステップに進む。
  - 1: {コメント} → 修正指示あり。指示に従い修正してから再度停止する。
- structure.md / tech.md のルールを常に遵守する。

## 作業ディレクトリ

- ワークフローの成果物は .kiro/specs/{API_ID}/ に格納する。
- API仕様書を修正して再度ワークフローを実行する場合は、既存ディレクトリを削除し新規作成する。
- 同一APIで仕様変更が発生した場合は .kiro/specs/{API_ID}_v{連番}/ としてバージョンを切る。
  - 例: API001_v2/（API001の仕様が変わった2回目の実行）
- 過去バージョンのspecsは参照用として残す（削除しない）。

---

## Phase1: 設計書作成

### 1-1. API仕様書とスキーマ定義の確認

1. 対象のAPI仕様書（docs/api/API{ID}_*.md）を読み込む。
2. 参照DBに記載されたスキーマ定義を確認する。
3. 作業ディレクトリ .kiro/specs/{API_ID}/ を作成する（すでにあればスキップ）。

### 1-2. diff.md の作成

対象APIの変更概要を .kiro/specs/{API_ID}/diff.md に記載する。

1. 新規APIの場合: 新規作成としてAPIの概要（Endpoint, Method, 主な処理）を記載する。
2. 既存APIの修正の場合: 修正内容の概要（何が変わったか、影響範囲）を記載する。

### 1-3. 実装計画（instructions.md）の作成

以下の内容を .kiro/specs/{API_ID}/instructions.md に出力する:

- 新規作成するクラスの一覧（FQCN）
- 修正するクラスの一覧（既存クラスへの追記）
- 各クラスの概要（何をするクラスか）
- 実装上の注意点やリスク

### 1-4. 【実行停止】

ユーザーの確認を待つ。

### 1-5. DD書の作成

instructions.md を元に、DD書を docs/dd/ 配下に作成する。
DD書のフォーマット・記載ルールは create_dd.md（別途作成するSteering）に従う。

#### DD書の種類と配置先（レイヤードアーキテクチャ）

| クラス種別 | 配置先 | 備考 |
|---|---|---|
| Controller | docs/dd/controller/ | エンドポイント定義・DTOマッピング |
| Service | docs/dd/service/ | ビジネスロジック |
| Mapper | docs/dd/mapper/ | SQL定義。末尾にEntity定義も記載 |
| Model | docs/dd/model/ | ドメインモデル |
| Enum | docs/dd/constant/ | 定数Enum |

### 1-6. 【実行停止】

ユーザーの確認を待つ。

### 1-7. indexの確認

- 今回作成/修正されたMapper内のSQLに対して不足しているindexがないかを確認する。
- 不足があれば docs/schema/ 内の当該テーブルのスキーマ定義にindexを追加し、マイグレーションも連動更新する。

### 1-8. instructionsの最終確認

- DD書への指摘修正により instructions.md の内容とDD書に乖離がないかを確認する。
- 乖離があれば instructions.md をDD書に合わせて修正する。

---

## Phase2: コード生成

### 2-1. 実装指示書（mk_instructions.md）の作成

DD書を確認し、以下の内容を .kiro/specs/{API_ID}/mk_instructions.md に出力する:

- 新規作成するクラスのFQCNと実装概要
- 修正するクラスのFQCNと変更内容
- Mapper SQL（全文）
- ビジネスロジックの核心部分（擬似コードまたは実コード）
- DTO/Entity はフィールド一覧テーブルで記載（コードブロック不要）

#### コードスニペットの方針

- import文は記載しない
- package宣言・クラスアノテーション等の定型部分は省略可
- 記載するのはロジックの核心部分のみ
- Mapper SQL は全文記載（省略不可）

### 2-2. 【実行停止】

ユーザーの確認を待つ。

### 2-3. コード生成

mk_instructions.md とDD書を元に、Javaコードを生成する。

- structure.md のパッケージ構成に従って配置する。
- tech.md のコーディング規約に従って記述する。
- 生成後にコンパイルエラーがないことを確認する。

### 2-4. 【実行停止】

ユーザーの確認を待つ。

### 2-5. instructionsの最終確認

- DD書への指摘修正により instructions.md の内容とDD書に乖離がないかを確認する。
- 乖離があれば instructions.md をDD書に合わせて修正する。

### 2-6. mk_instructions / DD書の最終確認

- コードへの指摘修正により mk_instructions.md の内容と生成コードに乖離がないかを確認する。
- 乖離があれば mk_instructions.md を生成コードに合わせて修正する。
- 生成コードへの修正によりDD書との乖離がないかを確認する。
- 乖離があればDD書を生成コードに合わせて修正する。

---

## Phase3: 単体テスト

### 3-1. テストケース（test.md）の作成

API仕様書（実装ではなく設計書）をベースにテストケースを作成する。

.kiro/specs/{API_ID}/test.md に以下を出力する:

- テストケース一覧（No / ケース名 / 確認観点 / 概要）
- 各ケースの詳細:
  - 前提データ（必要なSQL or API呼び出し）
  - curl コマンド（改行なし）
  - 期待値（Response JSON）
  - DB確認（確認SQL + 期待値）

#### テストケースの網羅ルール

| カテゴリ | 内容 |
|---------|------|
| 正常系 | 必須項目のみ / 全項目入力 |
| バリデーション | 必須チェック / 桁数チェック / フォーマットチェック |
| ビジネスエラー | 存在しないID(404) / 重複(400) |

#### 重要ルール

- テストケースの期待値はAPI仕様書から導出する。実装コードを見て期待値を決めない。
- curlは改行なしで記載する。

### 3-2. 【実行停止】

ユーザーの確認を待つ。テスト実行に進む場合は #local_test.md を添える:

0: #local_test.md 問題なし、テスト実行して

### 3-3. テスト実行

local_test.md（別途作成するSteering）の手順に従い、テストを実行する。

1. 既存プロセスを停止する
2. テスト用DBをクリーン&マイグレーションする
3. テスト用DB接続でアプリケーションを起動する
4. test.md のcurlを順番に実行し、期待値と比較する
5. 結果を .kiro/specs/{API_ID}/test_result.md に出力する
6. テスト完了後、アプリケーションを停止する

### 3-4. 【実行停止】

テスト結果を確認する。全ケースOKならワークフロー完了。NGがあればコードを修正して再テストする。

---

## テスト失敗時の対応

- AIはテスト失敗時に自動でコードを修正して再テストを試みる。
- テストケースの期待値が誤っている場合は test.md を修正してよい。
- コードの修正後は DD書との整合性を確認し、乖離があれば DD書も修正する。`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6 */}
      <WorkflowSection title="1本目のAPIで全工程を通す" id="section-6" number={6}>
        <WorkflowSection title="どのAPIから始めるか" subNumber="6.1">
          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              最もシンプルなAPIから始めてください。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              登録API（POST）が最適です。理由:
            </Typography>
          </WorkflowInfoBox>
          <WorkflowList
            items={[
              '依存が少ない（他のAPIのデータを前提としない）',
              '処理が直線的（INSERT 1回）',
              'テストが簡単（1回のcurlで完結）',
            ]}
          />
          <WorkflowText>
            複雑なAPI（一覧取得、更新等）は2本目以降に回してください。1本目はワークフロー自体の検証が目的です。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="ワークフローの起動" subNumber="6.2">
          <WorkflowChatBubble role="human">
            #api_dev_workflow.md API001（TODO登録）のワークフローを開始してください。
          </WorkflowChatBubble>
        </WorkflowSection>

        <WorkflowSection title="各フェーズでの確認ポイント" subNumber="6.3">
          <WorkflowText>Phase1 確認時:</WorkflowText>
          <WorkflowChecklist
            groups={[
              {
                title: 'Phase1 確認チェックリスト',
                items: [
                  'instructions.md のクラス一覧に漏れはないか',
                  'DD書の処理セクションが具体的か（「登録する」だけで終わっていないか）',
                  'DD書のMapper SQLが正しいか',
                  'structure.md のパッケージ構成に沿ったクラス配置になっているか',
                ],
              },
            ]}
          />

          <WorkflowText>Phase2 確認時:</WorkflowText>
          <WorkflowChecklist
            groups={[
              {
                title: 'Phase2 確認チェックリスト',
                items: [
                  'tech.md のコーディング規約に従っているか',
                  'Lombokアノテーションが正しく使われているか',
                  'コンパイルエラーがないか',
                  'DD書の処理がコードに正しく反映されているか',
                ],
              },
            ]}
          />

          <WorkflowText>Phase3 確認時:</WorkflowText>
          <WorkflowChecklist
            groups={[
              {
                title: 'Phase3 確認チェックリスト',
                items: [
                  'テストケースがAPI仕様書の全パターンを網羅しているか',
                  '期待値がAPI仕様書と一致しているか（実装から導出していないか）',
                  '全ケースがOKか',
                ],
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="よくある問題と対処" subNumber="6.4">
          <WorkflowTable
            headers={['問題', 'よくある原因', '対処']}
            rows={[
              [
                'Phase2でパッケージ配置が違う',
                'structure.md の記載不足',
                'structure.md に具体例を追記',
              ],
              [
                'コード生成で命名規則が違う',
                'tech.md の記載不足',
                'tech.md に命名規則の具体例を追記',
              ],
              [
                'テストで401が返る',
                'Spring Securityの全許可設定漏れ',
                'SecurityConfigを確認（「導入編」で解決済みのはず）',
              ],
              [
                'テストでDB接続エラー',
                'Docker未起動 or DB名間違い',
                'docker compose ps で確認、テスト用DB名を確認',
              ],
              [
                'テストで期待値と実際が違う',
                'DD書の処理が間違っている or テスト期待値が間違っている',
                'API仕様書と突合して原因を特定',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション7 */}
      <WorkflowSection title="ワークフローを改善する" id="section-7" number={7}>
        <WorkflowSection title="なぜ改善が必要か" subNumber="7.1">
          <WorkflowText>
            1本目のAPIを通して、api_dev_workflow.md の不備が見つかるはずです。
          </WorkflowText>
          <WorkflowList
            items={[
              'DD書のフォーマットが不十分でAIが迷う部分',
              'テスト実行手順のDB名やポート番号の差異',
              'Phase2でAIが遵守しないルール',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="改善サイクル" subNumber="7.2">
          <WorkflowProcedureFlow
            steps={[
              { label: '1本目のAPI実装', sub: 'ワークフロー初版で実行' },
              { label: '問題点を特定', sub: '何がうまくいかなかったか', variant: 'check' },
              { label: 'ワークフローを修正', sub: 'api_dev_workflow.md にルール追記' },
              {
                label: '2本目のAPI実装',
                sub: '改善版で実行 → 品質が安定するまで繰り返す',
                variant: 'complete',
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="典型的な改善例" subNumber="7.3">
          <WorkflowTable
            headers={['問題', 'api_dev_workflow.md への追記']}
            rows={[
              [
                'DD書のMapper SQLが毎回フォーマット違い',
                '「SQLはインデント付きで記載する。テーブル結合時のみ別名を付ける」を追記',
              ],
              [
                'Phase2で不要なクラスが生成される',
                '「DD書に記載のないクラスは作成しない」を禁止事項に追記',
              ],
              ['テスト実行でアプリ起動待ちが足りない', 'sleep 20 を sleep 30 に変更'],
              [
                'Entity に余計なフィールドが追加される',
                '「Entity のフィールドはスキーマ定義に合わせる。定義にないフィールドを追加しない」を追記',
              ],
            ]}
          />

          <WorkflowTipBox title="APIを重ねるたびにワークフローが育つ">
            <WorkflowText>これがこの章の本当のゴールです。</WorkflowText>
            <WorkflowText>
              1本目のAPIはワークフローの「初版」で実行します。必ず問題が出ます。2本目は改善版で実行し、1本目で起きた問題が解消されているか確認します。3本目ではさらに洗練されます。
            </WorkflowText>
            <WorkflowText>
              最終的に、全CRUDのAPIが完了する頃には、api_dev_workflow.md
              はあなたのプロジェクトに最適化された高品質なワークフローSteering になっています。
            </WorkflowText>
            <WorkflowText>これが「AIの出力品質はルールの品質で決まる」の集大成です。</WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション8 */}
      <WorkflowSection title="残りのAPIを実装する" id="section-8" number={8}>
        <WorkflowSection title="進め方" subNumber="8.1">
          <WorkflowText>
            1本目の体験を踏まえ、残りのAPIも同じワークフローで実装してください。
          </WorkflowText>

          <WorkflowChatBubble role="human">
            #api_dev_workflow.md API002（TODO更新）のワークフローを開始して
          </WorkflowChatBubble>

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              各APIの実行後に「うまくいかなかったこと」をメモし、api_dev_workflow.md
              に反映してください。このPDCA（ワークフロー実行→問題発見→ルール改善→次のAPIで確認）を繰り返すことで、ワークフローの品質が安定します。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="全API完了の基準" subNumber="8.2">
          <WorkflowList
            items={[
              '全APIのPhase3（単体テスト）が全ケースOKであること',
              'アプリケーション全体がビルドエラーなく起動すること',
              '各APIを個別にcurlで呼び出して正常に動作すること',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション9 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-9" number={9}>
        <WorkflowDirectoryTree
          root="team-todo/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'requirements',
                  children: [{ name: '...（省略）' }],
                },
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md' },
                    { name: 'structure.md' },
                    { name: 'tech.md' },
                    { name: 'create_schema.md' },
                    { name: 'api_specs_rule.md' },
                    { name: 'api_dev_workflow.md', desc: '今回作成', highlight: '#e65100' },
                    { name: 'create_dd.md', desc: '今回作成', highlight: '#e65100' },
                    { name: 'local_test.md', desc: '今回作成', highlight: '#e65100' },
                  ],
                },
                {
                  name: 'specs',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [
                    {
                      name: 'API001',
                      children: [
                        { name: 'instructions.md' },
                        { name: 'mk_instructions.md' },
                        { name: 'test.md' },
                        { name: 'test_result.md' },
                      ],
                    },
                    { name: 'API002', children: [{ name: '...' }] },
                  ],
                },
              ],
            },
            {
              name: 'docs',
              color: '#2e7d32',
              children: [
                { name: 'schema/' },
                { name: 'api/' },
                {
                  name: 'dd',
                  desc: '今回生成',
                  highlight: '#1976d2',
                  children: [
                    { name: 'controller/' },
                    { name: 'service/' },
                    { name: 'mapper/' },
                    { name: 'model/' },
                    { name: 'constant/' },
                  ],
                },
              ],
            },
            {
              name: 'src',
              color: '#e65100',
              children: [
                {
                  name: 'main/java/com/example/teamtodo',
                  children: [
                    { name: 'TeamTodoApplication.java' },
                    { name: 'config', children: [{ name: 'SecurityConfig.java' }] },
                    {
                      name: 'controller',
                      desc: '今回生成',
                      highlight: '#1976d2',
                      children: [{ name: 'TodoController.java' }, { name: 'dto/' }],
                    },
                    {
                      name: 'service',
                      desc: '今回生成',
                      highlight: '#1976d2',
                      children: [{ name: 'TodoService.java' }],
                    },
                    {
                      name: 'repository',
                      desc: '今回生成',
                      highlight: '#1976d2',
                      children: [{ name: 'TodoMapper.java' }],
                    },
                    {
                      name: 'entity',
                      desc: '今回生成',
                      highlight: '#1976d2',
                      children: [{ name: 'TodoEntity.java' }],
                    },
                    {
                      name: 'model',
                      desc: '今回生成',
                      highlight: '#1976d2',
                      children: [{ name: 'Todo.java' }],
                    },
                    {
                      name: 'constant',
                      desc: '今回生成',
                      highlight: '#1976d2',
                      children: [{ name: 'TodoStatus.java' }],
                    },
                  ],
                },
                {
                  name: 'main/resources',
                  children: [{ name: 'application.yml' }, { name: 'db/migration/' }],
                },
              ],
            },
            { name: 'build.gradle' },
            { name: 'settings.gradle' },
            { name: 'docker-compose.yml' },
            { name: '...' },
          ]}
        />
      </WorkflowSection>

      {/* セクション10 */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-10" number={10}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: '最もシンプルなAPIから始める',
              effect: 'ワークフロー自体の検証が目的。複雑なAPIは問題の切り分けが困難',
            },
            {
              practice: '各停止ポイントで必ず確認する',
              effect: '設計ミスがコード全体に波及するのを早期に防ぐ',
            },
            {
              practice: '問題が起きたらワークフローを改善してから次に進む',
              effect:
                '同じ問題を何度も手動で直すのは非効率。ルールに反映すれば以後は自動で正しくなる',
            },
            {
              practice: 'テストの期待値はAPI仕様書から導出する',
              effect: '実装のバグが期待値に混入するのを防ぐ',
            },
            {
              practice: 'DD書の処理を詳細に書く',
              effect: 'Phase2のコード品質はDD書の精度で決まる',
            },
            {
              practice: 'テスト後にアプリを停止する',
              effect: 'ポート占有で次のテストが失敗する',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: 'ワークフローなしでAPIを実装する',
              effect:
                '毎回異なるスタイルのコードが生成され、統一感が崩れる。ワークフローSteeringで手順を標準化する',
            },
            {
              practice: '停止ポイントを全て 0: で素通りする',
              effect:
                '設計ミスが蓄積し、テストフェーズで大量のバグが発覚する。最低限 instructions と DD書の方針を確認する',
            },
            {
              practice: '1本目の問題を放置して2本目に進む',
              effect:
                '同じ問題が繰り返し発生し、修正の手間が倍増する。問題をワークフローに反映してから次に進む',
            },
            {
              practice: '複雑なAPIから始める',
              effect:
                'ワークフローの問題とAPIの複雑さが混在し、原因の切り分けが困難。シンプルなCRUDから始める',
            },
            {
              practice: 'コードを手動修正してDD書を放置する',
              effect:
                'DD書とコードが乖離し、次回のワークフロー実行時にAIが古いDD書を参照する。コード修正後はDD書も必ず合わせる',
            },
            {
              practice: 'テスト失敗時に実装を見てテスト期待値を合わせる',
              effect:
                '実装のバグをテストが見逃す（テストの意味がなくなる）。API仕様書と突合して正しい期待値を判断する',
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション11 */}
      <WorkflowSection title="この章で学んだこと" id="section-11" number={11}>
        <WorkflowTable
          headers={['学び', '内容']}
          rows={[
            [
              'ワークフローSteeringで手順を自動化',
              '1APIの実装に必要な全工程を1ファイルで定義できる',
            ],
            ['停止ポイントは品質ゲート', 'AIの自動進行に任せず、要所で人間が確認する仕組み'],
            ['テストは設計書ベース', '実装から独立した期待値でバグを検出する'],
            [
              'ワークフローは「育てるもの」',
              'APIを重ねるたびに改善し、最終的にプロジェクトに最適化される',
            ],
          ]}
        />

        <WorkflowInfoBox type="preview">
          <Typography variant="body2" fontWeight={600}>
            次の記事: 実装編 — E2Eテスト
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            次の記事では以下を行います:
          </Typography>
          <WorkflowList
            items={[
              '全CRUDのAPIが単体テストまで完了した後に実施する',
              '複数APIを組み合わせた業務シナリオで機能横断の整合性を検証する',
              'E2Eテストケース生成ルール（create_e2e_testcase.md）を作成する',
            ]}
          />
        </WorkflowInfoBox>
      </WorkflowSection>

      <WorkflowLinkCard
        title="実装編 — E2Eテスト"
        description="E2Eテストケース生成ルールを作成し、業務シナリオテストを全件OK達成する"
        path="/devdocs/handson/e2e"
      />

      <WorkflowChecklist
        groups={[
          {
            title: '最終チェックリスト',
            items: [
              '.kiro/steering/api_dev_workflow.md が作成されている',
              '.kiro/steering/create_dd.md が作成されている',
              '.kiro/steering/local_test.md が作成されている',
              '少なくとも1本のAPIで Phase1→Phase2→Phase3 を完了している',
              'アプリケーションがビルドエラーなく起動する',
              '単体テストが全ケースOKである',
              'ワークフローを1回以上改善した実績がある',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonApiWorkflow;
